import { authFetch } from '../providers/AuthProvider';

const request = async (url, options = {}) => {
    const response = await authFetch(url, {
        credentials: 'include',
        ...options,
        headers: options.body instanceof FormData
            ? options.headers
            : { 'Content-Type': 'application/json', ...options.headers },
    });
    const responseText = await response.text();
    let data = {};
    if (responseText) {
        try {
            data = JSON.parse(responseText);
        } catch {
            if (!response.ok) {
                throw new Error('The server could not complete the inbound booking request. Please try again.');
            }
            throw new Error('The server returned an invalid inbound booking response.');
        }
    }
    if (!response.ok) {
        throw new Error(data.error || 'Inbound booking request failed');
    }
    return data;
};

export const listInboundBookings = (customerId) => {
    const query = customerId ? `?customer_id=${encodeURIComponent(customerId)}` : '';
    return request(`/inbound_bookings${query}`);
};

export const getInboundBooking = (bookingId) => request(`/inbound_bookings/${bookingId}`);

export const downloadInboundBookingLabels = async (bookingId) => {
    const response = await authFetch(`/inbound_bookings/${bookingId}/labels`, {
        credentials: 'include',
    });
    if (!response.ok) {
        let message = 'Inbound stock labels could not be generated';
        try {
            const data = await response.json();
            message = data.error || message;
        } catch {
            // Keep the fallback when the server response is not JSON.
        }
        throw new Error(message);
    }
    const disposition = response.headers.get('Content-Disposition') || '';
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
        || `inbound-${bookingId.slice(0, 8)}-labels.zpl`;
    return { blob: await response.blob(), filename };
};

export const createInboundBooking = (booking) => request('/inbound_bookings', {
    method: 'POST',
    body: JSON.stringify(booking),
});

export const updateInboundBooking = (bookingId, booking) => request(`/inbound_bookings/${bookingId}`, {
    method: 'PUT',
    body: JSON.stringify(booking),
});

export const changeInboundBookingStatus = (bookingId, status, note) => request(`/inbound_bookings/${bookingId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, note }),
});

export const cancelInboundBooking = (bookingId) => request(`/inbound_bookings/${bookingId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({}),
});

export const addInboundBookingComment = (bookingId, comment) => request(`/inbound_bookings/${bookingId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ comment }),
});

export const uploadInboundBookingPhoto = (bookingId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/inbound_bookings/${bookingId}/photos`, {
        method: 'POST',
        body: formData,
    });
};

export const deleteInboundBookingPhoto = (bookingId, photoId) => request(
    `/inbound_bookings/${bookingId}/photos/${photoId}`,
    { method: 'DELETE' },
);

export const uploadInboundBookingPackingList = (bookingId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/inbound_bookings/${bookingId}/packing-list`, {
        method: 'POST',
        body: formData,
    });
};

export const deleteInboundBookingPackingList = (bookingId) => request(
    `/inbound_bookings/${bookingId}/packing-list`,
    { method: 'DELETE' },
);

export const downloadInboundBookingPackingList = async (bookingId) => {
    const response = await authFetch(`/inbound_bookings/${bookingId}/packing-list`, {
        credentials: 'include',
    });
    if (!response.ok) {
        let message = 'Packing list could not be downloaded';
        try {
            const data = await response.json();
            message = data.error || message;
        } catch {
            // Keep the fallback when the server response is not JSON.
        }
        throw new Error(message);
    }
    const disposition = response.headers.get('Content-Disposition') || '';
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
        || `packing-list-${bookingId.slice(0, 8)}.pdf`;
    return { blob: await response.blob(), filename };
};

export const listBookingProducts = async (customerId) => {
    const query = customerId ? `?customer_id=${encodeURIComponent(customerId)}` : '';
    const data = await request(`/enhanced_warehouse/products${query}`);
    return data.products || data.data || [];
};
